import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Loader2, CheckCircle, DollarSign, Percent, Clock, CreditCard } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function OfferSelectionPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [selecting, setSelecting] = useState(null);
  const [selectedDone, setSelectedDone] = useState(false);
  const [fallbackMessage, setFallbackMessage] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/api/offers/${token}`)
      .then(res => { setData(res.data); })
      .catch(() => { setError('This offer link is invalid or has expired.'); })
      .finally(() => { setLoading(false); });
  }, [token]);

  const handleSelect = async (index) => {
    setSelecting(index);
    try {
      const res = await axios.post(`${API_URL}/api/offers/${token}/select`, { offer_index: index });
      setSelectedDone(true);
      setData(prev => ({ ...prev, already_selected: true, selected_index: index, offer_status: res.data.offer_status }));

      if (res.data.redirect_url) {
        window.location.href = res.data.redirect_url;
      } else {
        setFallbackMessage('Your application is being finalized. A specialist will contact you shortly.');
      }
    } catch {
      setError('Failed to process selection. Please try again.');
    } finally {
      setSelecting(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="offer-page-loading">
        <Loader2 className="h-10 w-10 animate-spin text-slate-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4" data-testid="offer-page-error">
        <Card className="max-w-md w-full"><CardContent className="p-8 text-center">
          <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-red-400 text-2xl">!</span>
          </div>
          <p className="text-slate-600">{error}</p>
        </CardContent></Card>
      </div>
    );
  }

  const alreadySelected = data?.already_selected || selectedDone;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="offer-selection-page">
      {/* Header */}
      <div className="bg-[#1a365d] text-white py-8 px-4 text-center">
        <h1 className="text-2xl font-bold tracking-tight">Prestigious Travel Club</h1>
        <p className="text-white/70 text-sm mt-1">Financing Options</p>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Greeting */}
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-slate-800">
            Hello{data?.client_name ? `, ${data.client_name}` : ''}
          </h2>
          <p className="text-slate-500 text-sm mt-1">
            {alreadySelected
              ? 'You have already selected an offer. Thank you!'
              : 'Please review and select the financing option that works best for you.'}
          </p>
        </div>

        {/* Offers */}
        <div className="space-y-3" data-testid="offer-list">
          {data?.offers?.map((offer, idx) => {
            const isSelected = data.selected_index === idx;
            return (
              <Card key={offer.id || `offer-${idx}`} className={`transition-all ${isSelected ? 'ring-2 ring-emerald-500 bg-emerald-50' : ''}`} data-testid={`offer-card-${idx}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-sm font-bold text-[#1a365d]">Option {idx + 1}</span>
                        {isSelected && <Badge className="bg-emerald-500 text-white text-[10px]">Selected</Badge>}
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        <div className="flex items-center gap-2">
                          <DollarSign className="h-4 w-4 text-slate-400" />
                          <div>
                            <p className="text-[10px] text-slate-400 uppercase">Amount</p>
                            <p className="text-sm font-bold text-slate-800">${(offer.approved_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Percent className="h-4 w-4 text-slate-400" />
                          <div>
                            <p className="text-[10px] text-slate-400 uppercase">APR</p>
                            <p className="text-sm font-bold text-slate-800">{offer.apr}%</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4 text-slate-400" />
                          <div>
                            <p className="text-[10px] text-slate-400 uppercase">Term</p>
                            <p className="text-sm font-bold text-slate-800">{offer.term} months</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <CreditCard className="h-4 w-4 text-slate-400" />
                          <div>
                            <p className="text-[10px] text-slate-400 uppercase">Payment</p>
                            <p className="text-sm font-bold text-slate-800">${(offer.monthly_payment || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}/mo</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex-shrink-0 pt-1">
                      {isSelected ? (
                        <CheckCircle className="h-8 w-8 text-emerald-500" />
                      ) : !alreadySelected ? (
                        <Button
                          onClick={() => handleSelect(idx)}
                          disabled={selecting !== null}
                          className="bg-[#d4a84b] hover:bg-[#c49a3d] text-[#1a365d] font-bold"
                          data-testid={`select-offer-${idx}`}
                        >
                          {selecting === idx ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Select'}
                        </Button>
                      ) : null}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Post-selection */}
        {alreadySelected && (
          <div className="mt-6 text-center" data-testid="selection-confirmed">
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-6">
              <CheckCircle className="h-10 w-10 text-emerald-500 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-emerald-800">Offer Selected</h3>
              <p className="text-sm text-emerald-600 mt-1">
                {fallbackMessage || 'A confirmation has been sent to your email. Our team will contact you shortly.'}
              </p>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="mt-8 text-center text-xs text-slate-400">
          <p>Prestigious Travel Club | Client Support</p>
          <p>clientsupport@prestigioustravelclub.org</p>
        </div>
      </div>
    </div>
  );
}
